/**
 * Web Push helpers — subscription + platform capability detection.
 *
 * The critical bit: `applicationServerKey` passed to pushManager.subscribe()
 * MUST be a Uint8Array, NOT the base64url VAPID string. Passing the raw string
 * throws on most browsers — which is why enabling push silently failed before.
 */

/** Convert a base64url VAPID public key to the Uint8Array the Push API requires. */
export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

/** HTTPS or localhost — required for service workers and Web Push. */
export function isSecurePushContext() {
  return typeof window !== 'undefined' && window.isSecureContext === true;
}

/** True only where Web Push can actually work in the current context. */
export function isPushSupported() {
  return (
    isSecurePushContext() &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Human-readable reason when push cannot work in this browser tab.
 * @returns {string|null} null when push is supported
 */
export function getPushUnsupportedReason() {
  if (typeof window === 'undefined') return 'Navigateur non compatible.';
  if (!isSecurePushContext()) {
    return 'Les notifications push nécessitent une connexion sécurisée (HTTPS).';
  }
  if (!('serviceWorker' in navigator)) return 'Ce navigateur ne prend pas en charge les notifications push.';
  if (!('PushManager' in window)) return 'Ce navigateur ne prend pas en charge les notifications push.';
  if (!('Notification' in window)) return 'Ce navigateur ne prend pas en charge les notifications.';
  return null;
}

/** iOS/iPadOS (incl. iPadOS reporting as Mac with touch). */
export function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOSClassic = /iPad|iPhone|iPod/.test(ua);
  const iPadOS13plus = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iOSClassic || iPadOS13plus;
}

/** Is the app running as an installed PWA (standalone display mode)? */
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari exposes navigator.standalone instead of display-mode.
    window.navigator.standalone === true
  );
}

/**
 * iOS only delivers Web Push to an **installed** PWA (Home Screen). In a Safari
 * tab there is no push — the user must "Add to Home Screen" first.
 */
export function iosNeedsInstallForPush() {
  return isIOS() && !isStandalone();
}

/**
 * Subscribe the browser to Web Push and return the subscription JSON.
 * @param {string} vapidPublicKey base64url VAPID public key from the server
 */
async function waitForServiceWorker(timeoutMs = 15000) {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service worker non disponible sur cet appareil.');
  }
  const existing = await navigator.serviceWorker.getRegistration();
  if (!existing) {
    throw new Error(
      'Le service de notifications n’a pas pu démarrer. Rechargez la page ou vérifiez que vous utilisez HTTPS.'
    );
  }
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, reject) => {
      setTimeout(() => reject(new Error(
        'Le service de notifications met trop de temps à démarrer. Rechargez la page et réessayez.'
      )), timeoutMs);
    }),
  ]);
}

export async function subscribeToPush(vapidPublicKey) {
  const unsupported = getPushUnsupportedReason();
  if (unsupported) throw new Error(unsupported);
  if (!vapidPublicKey) {
    throw new Error('Configuration des notifications indisponible sur le serveur.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Permission refusée pour les notifications.');
  }

  const registration = await waitForServiceWorker();
  const existing = await registration.pushManager.getSubscription();
  if (existing) return existing.toJSON();

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey), // <-- the fix
  });
  return subscription.toJSON();
}

/** Remove the current push subscription, returning its endpoint (or null). */
export async function unsubscribeFromPush() {
  if (!('serviceWorker' in navigator)) return null;
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return null;
  const { endpoint } = subscription;
  await subscription.unsubscribe();
  return endpoint;
}
